import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { getMeetings, getCommittees } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faPlus,
  faCalendarAlt,
  faUsers,
  faClock,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import LotusIcon from "../components/LotusIcon";

const MeetingsList = () => {
  const { isAuthenticated } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and sort state
  const [filters, setFilters] = useState({
    search: "",
    committee: "",
    startDate: "",
    endDate: "",
    sort: "-date", // Default sort by date descending
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch meetings with filters
        const meetingsData = await getMeetings(filters);
        setMeetings(meetingsData || []);

        // Fetch committees for filter dropdown
        const committeesData = await getCommittees();
        setCommittees(committeesData || []);
      } catch (err) {
        console.error("Error fetching meetings:", err);
        setError("Failed to load meetings. Please try again later.");
        setMeetings([]);
        setCommittees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      committee: "",
      startDate: "",
      endDate: "",
      sort: "-date",
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getCommitteeBadge = (committee) => {
    const badgeMap = {
      Executive: "primary",
      Finance: "warning",
      Internal: "success",
      Operations: "info",
      Other: "secondary",
    };

    return badgeMap[committee] || "secondary";
  };

  return (
    <Container>
      <div className="section-title d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <LotusIcon className="me-3" size={32} />
          <h1 className="mb-0">Meetings</h1>
        </div>
        <Link to="/meetings/new">
          <Button
            variant="primary"
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
              fontWeight: "600",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Meeting
          </Button>
        </Link>
      </div>

      <Card
        className="mb-4 shadow-sm border-0"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <Card.Header
          style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
        >
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faFilter}
              className="me-2"
              style={{ color: "#d9669b" }}
            />
            Filter Meetings
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Group controlId="search">
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      background: "#fff0f5",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={{ color: "#d9669b" }}
                    />
                  </InputGroup.Text>
                  <Form.Control
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Search by title or description"
                    style={{ borderLeft: "none" }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Group controlId="committee">
                <Form.Label>Committee</Form.Label>
                <Form.Select
                  name="committee"
                  value={filters.committee}
                  onChange={handleFilterChange}
                >
                  <option value="">All Committees</option>
                  {committees.map((committee) => (
                    <option key={committee._id} value={committee._id}>
                      {committee.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="sort">
                <Form.Label>Sort By</Form.Label>
                <Form.Select
                  name="sort"
                  value={filters.sort}
                  onChange={handleFilterChange}
                >
                  <option value="-date">Date (Newest First)</option>
                  <option value="date">Date (Oldest First)</option>
                  <option value="title">Title (A-Z)</option>
                  <option value="-title">Title (Z-A)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="startDate">
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="endDate">
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3 d-flex align-items-end">
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                className="w-100"
                style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center my-5 lotus-loader">
          <div className="lotus-petals">
            <LotusIcon size={32} />
          </div>
          <p className="mt-3">Loading meetings...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : meetings.length === 0 ? (
        <div className="text-center py-5 section">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            style={{ fontSize: "3rem", color: "#d9669b", opacity: 0.5 }}
          />
          <h3 className="mt-3" style={{ color: "#d9669b" }}>
            No meetings found
          </h3>
          <p className="text-muted">
            Try adjusting your filters or create a new meeting
          </p>
          <Link to="/meetings/new">
            <Button
              variant="primary"
              style={{
                background: "linear-gradient(to right, #e66b8f, #d4af37)",
                border: "none",
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Create New Meeting
            </Button>
          </Link>
        </div>
      ) : (
        <Row>
          {meetings.map((meeting) => (
            <Col lg={4} md={6} className="mb-4" key={meeting._id}>
              <Card
                className="h-100 shadow-sm meeting-card border-0"
                style={{ borderRadius: "10px", overflow: "hidden" }}
              >
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <Badge
                    bg={getCommitteeBadge(meeting.committee?.name)}
                    className="sasc-badge"
                  >
                    {meeting.committee?.name || "No Committee"}
                  </Badge>
                  <small className="text-muted">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {meeting.duration} min
                  </small>
                </Card.Header>
                <Card.Body>
                  <Card.Title style={{ color: "#d9669b" }}>
                    {meeting.title}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {meeting.description?.substring(0, 100)}
                    {meeting.description?.length > 100 ? "..." : ""}
                  </Card.Text>
                  <div className="meeting-details mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="me-2"
                        style={{ color: "#d9669b" }}
                      />
                      <small>{formatDate(meeting.date)}</small>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="me-2"
                        style={{ color: "#d9669b" }}
                      />
                      <small>
                        {meeting.location || "No location specified"}
                      </small>
                    </div>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="me-2"
                        style={{ color: "#d9669b" }}
                      />
                      <small>{meeting.attendees?.length || 0} attendees</small>
                    </div>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <Link
                    to={`/meetings/${meeting._id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Button
                      variant="outline-primary"
                      className="w-100"
                      style={{
                        borderColor: "#f5c2c2",
                        color: "#d9669b",
                        transition: "all 0.3s ease",
                      }}
                    >
                      View Details
                    </Button>
                  </Link>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default MeetingsList;
